import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { STATUSES } from '../../data/mockData';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import ConfirmDialog from '../shared/ConfirmDialog';
import { X, Video, Loader2 } from 'lucide-react';
import VisitBilling from './VisitBilling';

const fallbackType = { label: 'Appointment', color: '#6B7280' };
const fallbackStatus = { label: 'Unknown', color: '#6B7280' };

export default function AppointmentDetail({ appointment, onClose, onStatusChange, onDelete, onUpdate, typesMap = {} }) {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [startingVisit, setStartingVisit] = useState(false);
  const [visitError, setVisitError] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [editForm, setEditForm] = useState({
    type: appointment.type || '',
    date: appointment.start ? format(appointment.start, 'yyyy-MM-dd') : '',
    startTime: appointment.start ? format(appointment.start, 'HH:mm') : '',
    endTime: appointment.end ? format(appointment.end, 'HH:mm') : '',
    location: appointment.location || '',
    notes: appointment.notes || '',
  });
  const [collaborators, setCollaborators] = useState([]);
  const [loadingCollabs, setLoadingCollabs] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ date: '', startTime: '', endTime: '' });
  const [rescheduling, setRescheduling] = useState(false);

  const isOwner = !appointment.is_collaborator;

  const fetchCollaborators = useCallback(async () => {
    if (!currentOrg || !appointment?._raw?.id) return;
    if (appointment.is_collaborator) return; // Collaborator orgs get read-only view
    setLoadingCollabs(true);
    try {
      const data = await api.getCollaborators(currentOrg.id, appointment._raw.id);
      setCollaborators(data);
    } catch {
      // May not have access
    } finally {
      setLoadingCollabs(false);
    }
  }, [currentOrg, appointment]);

  useEffect(() => { fetchCollaborators(); }, [fetchCollaborators]);

  const handleRemoveCollaborator = async (collabId) => {
    if (!currentOrg) return;
    try {
      await api.removeCollaborator(currentOrg.id, appointment._raw.id, collabId);
      setCollaborators(prev => prev.filter(c => c.id !== collabId));
    } catch (err) {
      console.error('Failed to remove collaborator:', err);
    }
  };

  if (!appointment) return null;

  const type = typesMap[appointment.type] || fallbackType;
  const status = STATUSES[appointment.status] || fallbackStatus;

  const handleReschedule = async () => {
    if (!currentOrg || !rescheduleForm.date || !rescheduleForm.startTime || !rescheduleForm.endTime) return;
    setRescheduling(true);
    try {
      const newStartsAt = `${rescheduleForm.date}T${rescheduleForm.startTime}:00`;
      const newEndsAt = `${rescheduleForm.date}T${rescheduleForm.endTime}:00`;
      await api.updateAppointment(currentOrg.id, appointment.id, {
        starts_at: newStartsAt,
        ends_at: newEndsAt,
        status: 'pending',
      });
      onUpdate?.(appointment.id, {
        start: new Date(newStartsAt),
        end: new Date(newEndsAt),
        status: 'pending',
      });
      onStatusChange(appointment.id, 'pending');
      setShowReschedule(false);
    } catch (err) {
      console.error('Failed to reschedule:', err);
    } finally {
      setRescheduling(false);
    }
  };

  const handleCancel = async () => {
    if (!currentOrg) return;
    try {
      await api.updateAppointment(currentOrg.id, appointment.id, {
        status: 'cancelled',
        cancelled_reason: cancelReason || undefined,
      });
      onStatusChange(appointment.id, 'cancelled');
      setConfirmCancel(false);
    } catch (err) {
      console.error('Failed to cancel:', err);
    }
  };

  const handleDelete = async () => {
    if (!currentOrg) return;
    try {
      await api.deleteAppointment(currentOrg.id, appointment.id);
      onDelete?.(appointment.id);
      onClose();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleSaveEdit = async () => {
    if (!currentOrg) return;
    try {
      const payload = {
        location_name: editForm.location || null,
        description: editForm.notes || null,
      };

      // Include type if changed
      if (editForm.type && editForm.type !== appointment.type) {
        payload.type = editForm.type;
      }

      // Include date/time if changed
      const oldStart = appointment.start ? format(appointment.start, 'yyyy-MM-dd\'T\'HH:mm') : '';
      const newStart = editForm.date && editForm.startTime ? `${editForm.date}T${editForm.startTime}` : '';
      const newEnd = editForm.date && editForm.endTime ? `${editForm.date}T${editForm.endTime}` : '';
      const timeChanged = newStart && newStart !== oldStart;

      if (newStart) payload.starts_at = `${newStart}:00`;
      if (newEnd) payload.ends_at = `${newEnd}:00`;

      await api.updateAppointment(currentOrg.id, appointment.id, payload);

      const updates = {
        location: editForm.location,
        notes: editForm.notes,
        type: editForm.type,
      };
      if (newStart) updates.start = new Date(`${newStart}:00`);
      if (newEnd) updates.end = new Date(`${newEnd}:00`);

      onUpdate?.(appointment.id, updates);
      setEditing(false);
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  return (
    <div className="fixed inset-0 bg-overlay backdrop-blur-lg flex justify-end z-[200] animate-fade-in" onClick={onClose}>
      <div
        className="w-full sm:w-[460px] bg-primary h-full overflow-y-auto animate-slide-in border-l border-theme"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="p-6 md:p-7 flex items-start justify-between relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${type.color}, ${type.color}dd)` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-transparent pointer-events-none" />
          <div className="flex flex-col gap-2.5 relative">
            <span className="text-[20px] font-extrabold text-white tracking-tight">{type.label}</span>
            <span className="text-[11px] font-semibold px-3 py-1 rounded-full text-white self-start tracking-wide" style={{ background: status.color }}>
              {status.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="relative w-8 h-8 rounded-lg bg-white/15 backdrop-blur-lg text-white flex items-center justify-center hover:bg-white/25 hover:scale-105 transition-all"
          >
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 md:p-7 space-y-7">
          {/* Telehealth — opens (or rejoins) the video visit and emails/texts
              the client their join link the first time. */}
          {appointment.status !== 'cancelled' && (
            <div className="flex items-center justify-between gap-3 bg-accent-glow border border-strong rounded-xl p-4">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-primary-c flex items-center gap-1.5">
                  <Video size={14} className="text-accent-light" /> Video visit
                </div>
                <p className="text-[11px] text-muted-c mt-0.5">
                  Secure telehealth with live captions and translation. The client gets the join link automatically.
                </p>
                {visitError && <p className="text-[11px] text-red-400 mt-1">{visitError}</p>}
              </div>
              <button
                onClick={async () => {
                  setStartingVisit(true);
                  setVisitError('');
                  try {
                    const session = await api.createTelehealthSession(currentOrg.id, { appointment_id: appointment.id });
                    navigate(`/telehealth/${session.id}`);
                  } catch (err) {
                    setVisitError(err.message || 'Could not start the visit');
                    setStartingVisit(false);
                  }
                }}
                disabled={startingVisit}
                className="shrink-0 px-4 py-[8px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 flex items-center gap-1.5"
              >
                {startingVisit ? (<><Loader2 size={13} className="animate-spin" /> Opening…</>) : 'Start visit'}
              </button>
            </div>
          )}
          {/* Reschedule request banner */}
          {appointment.status === 'reschedule_requested' && isOwner && (
            <div className="bg-[rgba(217,70,239,0.08)] border border-[rgba(217,70,239,0.25)] rounded-xl p-4">
              <div className="text-[13px] font-semibold text-primary-c">Reschedule Requested</div>
              <p className="text-[12px] text-secondary-c mt-1">
                {appointment.client.name} is requesting to reschedule this appointment.
              </p>
              {appointment.notes?.startsWith('[Reschedule requested]') && (
                <p className="text-[11px] text-muted-c mt-1.5 italic">
                  "{appointment.notes.replace('[Reschedule requested] ', '')}"
                </p>
              )}

              {/* Show the client's requested time */}
              <div className="bg-tertiary rounded-[10px] p-3 mt-3 border border-theme">
                <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1">Client's Requested Time</div>
                <div className="text-[14px] font-bold text-primary-c">{format(appointment.start, 'EEEE, MMMM d, yyyy')}</div>
                <div className="text-[13px] font-semibold text-accent-light mt-0.5">
                  {format(appointment.start, 'h:mm a')} — {format(appointment.end, 'h:mm a')}
                </div>
              </div>

              {!showReschedule ? (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => onStatusChange(appointment.id, 'confirmed')}
                    className="px-4 py-[7px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5"
                  >
                    Approve This Time
                  </button>
                  <button
                    onClick={() => setShowReschedule(true)}
                    className="px-4 py-[7px] rounded-lg text-[12px] font-semibold text-secondary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                  >
                    Pick Different Time
                  </button>
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-3 gap-2.5">
                    <div>
                      <label className="block text-[11px] font-semibold text-muted-c mb-1">New Date</label>
                      <input type="date" className={inputClass} value={rescheduleForm.date} onChange={e => setRescheduleForm(p => ({ ...p, date: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-muted-c mb-1">Start</label>
                      <input type="time" className={inputClass} value={rescheduleForm.startTime} onChange={e => setRescheduleForm(p => ({ ...p, startTime: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-muted-c mb-1">End</label>
                      <input type="time" className={inputClass} value={rescheduleForm.endTime} onChange={e => setRescheduleForm(p => ({ ...p, endTime: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleReschedule}
                      disabled={rescheduling || !rescheduleForm.date || !rescheduleForm.startTime || !rescheduleForm.endTime}
                      className="px-4 py-[7px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all disabled:opacity-50"
                    >
                      {rescheduling ? 'Saving...' : 'Confirm New Time'}
                    </button>
                    <button
                      onClick={() => setShowReschedule(false)}
                      className="px-4 py-[7px] rounded-lg text-[12px] font-semibold text-muted-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Client */}
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Client</h4>
            <div className="flex gap-3.5 items-center bg-tertiary p-3.5 rounded-[10px] border border-theme">
              <div className="w-[46px] h-[46px] rounded-xl bg-accent-glow flex items-center justify-center text-[14px] font-bold text-accent-light flex-shrink-0">
                {appointment.client.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <div className="font-bold text-[14px] tracking-tight text-primary-c">{appointment.client.name}</div>
                {appointment.client.caseNumber && (
                  <div className="text-[12px] text-accent-light font-semibold mt-0.5">{appointment.client.caseNumber}</div>
                )}
              </div>
            </div>
          </div>

          {/* Appointment Type (edit mode) */}
          {editing && (
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Appointment Type</h4>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(typesMap).map(([key, t]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setEditForm(prev => ({ ...prev, type: key }))}
                    className={`flex items-center gap-[6px] px-3 py-[6px] rounded-full text-[11px] font-semibold transition-all border
                      ${editForm.type === key
                        ? 'text-white shadow-md border-transparent'
                        : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface'}`}
                    style={editForm.type === key ? { background: t.color, borderColor: t.color } : undefined}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: editForm.type === key ? 'white' : t.color }} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date & Time */}
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Date & Time</h4>
            {editing ? (
              <div className="grid grid-cols-3 gap-2.5">
                <div>
                  <label className="block text-[10px] font-semibold text-muted-c mb-1">Date</label>
                  <input type="date" className={inputClass} value={editForm.date} onChange={e => setEditForm(prev => ({ ...prev, date: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-muted-c mb-1">Start</label>
                  <input type="time" className={inputClass} value={editForm.startTime} onChange={e => setEditForm(prev => ({ ...prev, startTime: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-muted-c mb-1">End</label>
                  <input type="time" className={inputClass} value={editForm.endTime} onChange={e => setEditForm(prev => ({ ...prev, endTime: e.target.value }))} />
                </div>
              </div>
            ) : (
              <>
                <p className="text-primary-c">{format(appointment.start, 'EEEE, MMMM d, yyyy')}</p>
                <p className="text-[16px] font-bold text-accent-light tracking-tight mt-1">
                  {format(appointment.start, 'h:mm a')} — {format(appointment.end, 'h:mm a')}
                </p>
              </>
            )}
          </div>

          {/* Provider */}
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Provider</h4>
            <div className="flex gap-3 items-center bg-tertiary p-3 rounded-[10px] border border-theme">
              <div className="w-[34px] h-[34px] rounded-[9px] bg-accent-glow flex items-center justify-center text-[11px] font-bold text-accent-light">
                {appointment.staff.avatar}
              </div>
              <div>
                <div className="text-primary-c font-medium">{appointment.staff.name}</div>
                {appointment.staff.specialty && (
                  <div className="text-[12px] text-muted-c">{appointment.staff.specialty}</div>
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Location</h4>
            {editing ? (
              <input type="text" className={inputClass} value={editForm.location} onChange={e => setEditForm(prev => ({ ...prev, location: e.target.value }))} />
            ) : (
              <p className="text-primary-c">{appointment.location || 'Not specified'}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Notes</h4>
            {editing ? (
              <textarea className={inputClass} rows={3} value={editForm.notes} onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))} />
            ) : (
              <p className="text-[13px] leading-relaxed text-secondary-c bg-tertiary p-3.5 rounded-[10px] border border-theme">
                {appointment.notes || 'No notes'}
              </p>
            )}
          </div>

          {/* Collaborator indicator for partner appointments */}
          {appointment.is_collaborator && appointment.owning_org && (
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Shared By</h4>
              <div className="flex items-center gap-3 bg-accent-glow p-3 rounded-[10px] border border-accent-light/20">
                <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-[10px] font-bold text-white">
                  {appointment.owning_org.name?.split(' ').map(w => w[0]).join('').slice(0, 2) || '??'}
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-primary-c">{appointment.owning_org.name}</div>
                  <div className="text-[10px] text-accent-light font-medium">Network Partner</div>
                </div>
              </div>
            </div>
          )}

          {/* Collaborators section */}
          {isOwner && (collaborators.length > 0 || loadingCollabs) && (
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Collaborators</h4>
              {loadingCollabs ? (
                <div className="text-[12px] text-muted-c">Loading...</div>
              ) : (
                <div className="space-y-1.5">
                  {collaborators.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-2.5 bg-tertiary rounded-[10px] border border-theme group">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-accent-glow flex items-center justify-center text-[10px] font-bold text-accent-light flex-shrink-0">
                          {c.org?.name?.split(' ').map(w => w[0]).join('').slice(0, 2) || '??'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[12px] font-semibold text-primary-c truncate">{c.org?.name}</div>
                          <div className="text-[11px] text-muted-c">
                            {c.user ? `${c.user.first_name} ${c.user.last_name}` : 'Whole org'}
                            {c.role_label && <span className="text-accent-light ml-1">· {c.role_label}</span>}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveCollaborator(c.id)}
                        className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-md text-muted-c hover:text-danger hover:bg-danger-soft flex items-center justify-center transition-all text-[10px]"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Visit billing — one amount + payer; keeps the firm's PIP-remaining true */}
          {isOwner && appointment.status !== 'cancelled' && currentOrg?.type === 'medical_practice' && (
            <VisitBilling
              appointment={appointment}
              defaultCharge={typesMap[appointment.type]?.default_charge ?? null}
              onSaved={(updated) => onUpdate?.(appointment.id, { _raw: { ...(appointment._raw || {}), ...updated } })}
            />
          )}

          {/* Status */}
          <div>
            <h4 className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Update Status</h4>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(STATUSES).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => onStatusChange(appointment.id, key)}
                  className={`px-4 py-[7px] rounded-full text-[12px] font-semibold transition-all hover:-translate-y-0.5
                    ${appointment.status === key
                      ? 'text-white shadow-md'
                      : 'bg-tertiary text-secondary-c border border-strong hover:bg-hover-surface hover:text-primary-c'}`}
                  style={appointment.status === key ? { background: val.color, borderColor: val.color } : undefined}
                >
                  {val.label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-4 border-t border-theme">
            {editing ? (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 py-3 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 py-3 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="w-full py-3 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all"
                >
                  Edit Appointment
                </button>
                {appointment.status !== 'cancelled' && (
                  <button
                    onClick={() => setConfirmCancel(true)}
                    className="w-full py-3 rounded-[10px] text-[13px] font-semibold text-danger border border-[rgba(244,63,94,0.3)] hover:bg-danger-soft hover:border-[rgba(244,63,94,0.5)] transition-all"
                  >
                    Cancel Appointment
                  </button>
                )}
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="w-full py-3 rounded-[10px] text-[13px] font-semibold text-muted-c hover:text-danger transition-all"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cancel dialog */}
      <ConfirmDialog
        open={confirmCancel}
        title="Cancel Appointment"
        message={
          <div className="space-y-3">
            <p>Are you sure you want to cancel this appointment with {appointment.client.name}?</p>
            <textarea
              className={inputClass}
              rows={2}
              placeholder="Reason for cancellation (optional)"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
            />
          </div>
        }
        confirmLabel="Cancel Appointment"
        danger
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      />

      {/* Delete dialog */}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete Appointment"
        message="This will permanently remove this appointment. This action cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
